// SPDX-License-Identifier: AGPL-3.0-or-later

import ApplicationCommands, {
	type ApplicationCommand,
} from '@app/features/application_commands/state/ApplicationCommands';
import {type AutocompleteOption, isCommand} from '@app/features/channel/components/Autocomplete';
import styles from '@app/features/channel/components/AutocompleteCommand.module.css';
import {AutocompleteItem} from '@app/features/channel/components/AutocompleteItem';
import {getUserAvatarURL} from '@app/features/user/utils/AvatarUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type {RefObject} from 'react';

const LOADING_COMMANDS_DESCRIPTOR = msg({
	message: 'Loading bot commands…',
	comment: 'Shown at the bottom of the slash-command list while the commands of bots in this channel are loading.',
});
const SOME_COMMANDS_MISSING_DESCRIPTOR = msg({
	message: "Some bot commands couldn't be loaded.",
	comment: 'Shown at the bottom of the slash-command list when part of the bot command list failed to load.',
});

const MAX_OPTION_CHIPS = 4;

interface Props {
	onSelect: (option: AutocompleteOption) => void;
	keyboardFocusIndex: number;
	hoverIndex: number;
	options: Array<AutocompleteOption>;
	onMouseEnter: (index: number) => void;
	onMouseLeave: () => void;
	rowRefs?: RefObject<Array<HTMLButtonElement | null>>;
	getOptionId?: (index: number) => string;
}

interface ApplicationCommandRowProps {
	command: ApplicationCommand;
	id?: string;
	isActive: boolean;
	isKeyboardSelected: boolean;
	onSelect: () => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	innerRef?: (node: HTMLButtonElement | null) => void;
}

function ApplicationCommandRow({
	command,
	id,
	isActive,
	isKeyboardSelected,
	onSelect,
	onMouseEnter,
	onMouseLeave,
	innerRef,
}: ApplicationCommandRowProps) {
	const {application} = command;
	const chips = command.options.slice(0, MAX_OPTION_CHIPS);
	const hiddenChips = command.options.length - chips.length;
	return (
		<button
			type="button"
			id={id}
			className={`${styles.row} ${isActive ? styles.active : ''}`}
			onClick={onSelect}
			onPointerEnter={onMouseEnter}
			onPointerLeave={onMouseLeave}
			ref={innerRef}
			role="option"
			aria-selected={isKeyboardSelected}
			tabIndex={-1}
			data-flx="channel.autocomplete-command.application-row.select"
		>
			{application.botId != null ? (
				<img
					className={styles.avatar}
					src={getUserAvatarURL({id: application.botId, avatar: application.botAvatar}, false, 64)}
					width={32}
					height={32}
					alt=""
					draggable={false}
					data-flx="channel.autocomplete-command.application-row.avatar"
				/>
			) : (
				<span
					className={styles.avatar}
					aria-hidden={true}
					data-flx="channel.autocomplete-command.application-row.avatar"
				>
					{application.name.slice(0, 1)}
				</span>
			)}
			<span className={styles.body} data-flx="channel.autocomplete-command.application-row.body">
				<span className={styles.headline} data-flx="channel.autocomplete-command.application-row.headline">
					<span className={styles.name} data-flx="channel.autocomplete-command.application-row.name">
						{command.name}
					</span>
					{chips.map((option) => (
						<span
							key={option.name}
							className={`${styles.chip} ${option.required ? '' : styles.optionalChip}`}
							data-flx="channel.autocomplete-command.application-row.chip"
						>
							{option.name}
						</span>
					))}
					{hiddenChips > 0 ? (
						<span
							className={`${styles.chip} ${styles.optionalChip}`}
							data-flx="channel.autocomplete-command.application-row.more"
						>
							+{hiddenChips}
						</span>
					) : null}
				</span>
				<span className={styles.description} data-flx="channel.autocomplete-command.application-row.description">
					{command.description}
				</span>
			</span>
			<span className={styles.application} data-flx="channel.autocomplete-command.application-row.application">
				{application.name}
			</span>
		</button>
	);
}

const CatalogStatus = observer(() => {
	const {i18n} = useLingui();
	const status = ApplicationCommands.activeStatus;
	if (status !== 'loading' && status !== 'partial' && status !== 'error') return null;
	return (
		<div className={styles.status} role="status" data-flx="channel.autocomplete-command.status">
			{status === 'loading' ? i18n._(LOADING_COMMANDS_DESCRIPTOR) : i18n._(SOME_COMMANDS_MISSING_DESCRIPTOR)}
		</div>
	);
});

export const AutocompleteCommand = observer(
	({onSelect, keyboardFocusIndex, hoverIndex, options, onMouseEnter, onMouseLeave, rowRefs, getOptionId}: Props) => {
		const commands = options.filter(isCommand);
		return (
			<>
				{commands.map((option, index) => {
					const command = option.command;
					const innerRef = rowRefs
						? (node: HTMLButtonElement | null) => {
								rowRefs.current[index] = node;
							}
						: undefined;
					if (command.type === 'application') {
						return (
							<ApplicationCommandRow
								key={command.key}
								command={command}
								id={getOptionId?.(index)}
								isActive={index === keyboardFocusIndex || index === hoverIndex}
								isKeyboardSelected={index === keyboardFocusIndex}
								onSelect={() => onSelect(option)}
								onMouseEnter={() => onMouseEnter(index)}
								onMouseLeave={onMouseLeave}
								innerRef={innerRef}
								data-flx="channel.autocomplete-command.application-command-row.select"
							/>
						);
					}
					return (
						<AutocompleteItem
							key={command.name}
							id={getOptionId?.(index)}
							name={command.name}
							description={command.description}
							isKeyboardSelected={index === keyboardFocusIndex}
							isHovered={index === hoverIndex}
							onSelect={() => onSelect(option)}
							onMouseEnter={() => onMouseEnter(index)}
							onMouseLeave={onMouseLeave}
							innerRef={innerRef}
							data-flx="channel.autocomplete-command.autocomplete-item.select"
						/>
					);
				})}
				<CatalogStatus data-flx="channel.autocomplete-command.catalog-status" />
			</>
		);
	},
);
